/**
 * Shared UI primitives. Keep this file small and generic; feature-specific
 * components live next to their screens.
 */
/* eslint-disable react-refresh/only-export-components */
import {
  useEffect,
  useRef,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from 'react';
import { useT } from '../i18n/i18n';

// ── Icons (inline, stroke inherits currentColor) ──────────────────────────

function IconBase({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className ?? 'h-6 w-6'}
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

export const Icons = {
  home: (c?: string) => (
    <IconBase className={c}>
      <path d="M3 10.5 12 3l9 7.5" />
      <path d="M5 9.5V21h14V9.5" />
    </IconBase>
  ),
  track: (c?: string) => (
    <IconBase className={c}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 8v8M8 12h8" />
    </IconBase>
  ),
  history: (c?: string) => (
    <IconBase className={c}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3.5 2" />
    </IconBase>
  ),
  guide: (c?: string) => (
    <IconBase className={c}>
      <path d="M4 5a2 2 0 0 1 2-2h13v16H6a2 2 0 0 0-2 2z" />
      <path d="M4 19a2 2 0 0 1 2-2h13" />
    </IconBase>
  ),
  gear: (c?: string) => (
    <IconBase className={c}>
      <circle cx="12" cy="12" r="3.2" />
      <path d="M19 12a7 7 0 0 0-.14-1.4l2-1.55-2-3.46-2.36.95a7 7 0 0 0-2.42-1.4L13.7 2.6h-3.4l-.38 2.54a7 7 0 0 0-2.42 1.4l-2.36-.95-2 3.46 2 1.55A7 7 0 0 0 5 12c0 .48.05.94.14 1.4l-2 1.55 2 3.46 2.36-.95a7 7 0 0 0 2.42 1.4l.38 2.54h3.4l.38-2.54a7 7 0 0 0 2.42-1.4l2.36.95 2-3.46-2-1.55c.09-.46.14-.92.14-1.4Z" />
    </IconBase>
  ),
  heart: (c?: string) => (
    <IconBase className={c}>
      <path d="M12 20.5S4 15.3 4 9.8A4.3 4.3 0 0 1 8.3 5.5c1.6 0 3 .9 3.7 2.2a4.2 4.2 0 0 1 3.7-2.2A4.3 4.3 0 0 1 20 9.8c0 5.5-8 10.7-8 10.7Z" />
    </IconBase>
  ),
  phone: (c?: string) => (
    <IconBase className={c}>
      <path d="M5 4h4l2 5-2.5 1.5a12 12 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2Z" />
    </IconBase>
  ),
  calendar: (c?: string) => (
    <IconBase className={c}>
      <rect x="3.5" y="5" width="17" height="16" rx="2" />
      <path d="M3.5 10h17M8 2.5V6m8-3.5V6" />
    </IconBase>
  ),
  x: (c?: string) => (
    <IconBase className={c}>
      <path d="M6 6l12 12M18 6 6 18" />
    </IconBase>
  ),
  chevronRight: (c?: string) => (
    <IconBase className={c}>
      <path d="m9 5 7 7-7 7" />
    </IconBase>
  ),
};

// ── Screen header ─────────────────────────────────────────────────────────

export function ScreenHeader({
  title,
  subtitle,
  onSettings,
  right,
}: {
  title: string;
  subtitle?: string;
  onSettings?: () => void;
  right?: ReactNode;
}) {
  const t = useT();
  return (
    <header className="sticky top-0 z-20 border-b border-line bg-bg/90 backdrop-blur px-4 pt-3 pb-2.5">
      <div className="mx-auto flex max-w-lg items-center justify-between gap-3">
        <div className="min-w-0">
          <h1 className="truncate text-xl leading-tight">{title}</h1>
          {subtitle ? <p className="truncate text-sm text-ink-soft">{subtitle}</p> : null}
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {right}
          {onSettings ? (
            <button
              type="button"
              onClick={onSettings}
              aria-label={t.common.actions.settings}
              className="rounded-full p-2 text-ink-soft hover:bg-surface-2"
            >
              {Icons.gear('h-6 w-6')}
            </button>
          ) : null}
        </div>
      </div>
    </header>
  );
}

// ── Bottom sheet ──────────────────────────────────────────────────────────

const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function Sheet({
  open,
  onClose,
  title,
  children,
}: {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
}) {
  const t = useT();
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  // Kept in a ref so re-renders (parents pass inline onClose) never re-run
  // the focus effect and steal focus while the sheet is open.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCloseRef.current();
        return;
      }
      // Trap Tab within the dialog.
      if (e.key !== 'Tab') return;
      const dialog = dialogRef.current;
      if (!dialog) return;
      const focusables = dialog.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;
      const inside = active instanceof HTMLElement && dialog.contains(active);
      if (e.shiftKey && (active === first || !inside)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (active === last || !inside)) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
      previous?.focus();
    };
  }, [open]);

  if (!open) return null;
  return (
    <div
      ref={dialogRef}
      className="fixed inset-0 z-40"
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <button
        type="button"
        aria-label={t.common.actions.close}
        onClick={onClose}
        className="absolute inset-0 bg-black/40"
      />
      <div className="animate-float-up absolute inset-x-0 bottom-0 max-h-[88dvh] overflow-y-auto rounded-t-3xl border-t border-line bg-surface pb-[max(1rem,env(safe-area-inset-bottom))]">
        <div className="sticky top-0 flex items-center justify-between gap-2 rounded-t-3xl bg-surface px-5 pt-4 pb-2">
          <h2 className="text-lg">{title}</h2>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label={t.common.actions.close}
            className="rounded-full p-2 text-ink-soft hover:bg-surface-2"
          >
            {Icons.x('h-5 w-5')}
          </button>
        </div>
        <div className="px-5 pb-4">{children}</div>
      </div>
    </div>
  );
}

// ── Radio-group keyboard support ──────────────────────────────────────────

/**
 * Arrow-key navigation for role="radiogroup" containers with roving
 * tabindex. Attach to the container's onKeyDown; it finds the role="radio"
 * children, moves focus, and selects the focused option.
 */
export function radioGroupNav(
  e: ReactKeyboardEvent<HTMLElement>,
  selectAt: (index: number) => void,
): void {
  const radios = Array.from(e.currentTarget.querySelectorAll<HTMLElement>('[role="radio"]'));
  if (radios.length === 0) return;
  const focused = radios.findIndex((r) => r === document.activeElement);
  const checked = radios.findIndex((r) => r.getAttribute('aria-checked') === 'true');
  const from = focused >= 0 ? focused : Math.max(0, checked);
  let next: number;
  switch (e.key) {
    case 'ArrowRight':
    case 'ArrowDown':
      next = (from + 1) % radios.length;
      break;
    case 'ArrowLeft':
    case 'ArrowUp':
      next = (from - 1 + radios.length) % radios.length;
      break;
    case 'Home':
      next = 0;
      break;
    case 'End':
      next = radios.length - 1;
      break;
    default:
      return;
  }
  e.preventDefault();
  radios[next].focus();
  selectAt(next);
}

/** Roving tabindex: only the checked radio (or the first, when none is checked) is tabbable. */
export function radioTabIndex(checked: boolean, index: number, anyChecked: boolean): 0 | -1 {
  if (checked) return 0;
  return !anyChecked && index === 0 ? 0 : -1;
}

// ── Segmented control ─────────────────────────────────────────────────────

export function Segmented<T extends string>({
  value,
  onChange,
  options,
  label,
}: {
  value: T;
  onChange: (v: T) => void;
  options: { value: T; label: string }[];
  label?: string;
}) {
  return (
    <div
      role="radiogroup"
      aria-label={label}
      onKeyDown={(e) => radioGroupNav(e, (i) => onChange(options[i].value))}
      className="flex w-full rounded-full border border-line bg-surface-2 p-1"
    >
      {options.map((o, i) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={value === o.value}
          tabIndex={radioTabIndex(value === o.value, i, options.some((x) => x.value === value))}
          onClick={() => onChange(o.value)}
          className={`flex-1 rounded-full px-3 py-2 text-sm font-bold transition-colors ${
            value === o.value ? 'bg-primary text-on-primary shadow' : 'text-ink-soft'
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

// ── Tab bar ───────────────────────────────────────────────────────────────

export type Tab = 'home' | 'track' | 'history' | 'guide' | 'settings' | 'privacy';

export function TabBar({ tab, onChange }: { tab: Tab; onChange: (t: Tab) => void }) {
  const t = useT();
  const items: { key: Tab; label: string; icon: (c?: string) => ReactNode }[] = [
    { key: 'home', label: t.common.tabs.home, icon: Icons.home },
    { key: 'track', label: t.common.tabs.track, icon: Icons.track },
    { key: 'history', label: t.common.tabs.history, icon: Icons.history },
    { key: 'guide', label: t.common.tabs.guide, icon: Icons.guide },
  ];
  return (
    <nav className="fixed inset-x-0 bottom-0 z-30 border-t border-line bg-surface/95 backdrop-blur pb-[env(safe-area-inset-bottom)]">
      <div className="mx-auto flex max-w-lg">
        {items.map((item) => {
          const active = tab === item.key;
          return (
            <button
              key={item.key}
              type="button"
              onClick={() => onChange(item.key)}
              aria-current={active ? 'page' : undefined}
              className={`flex flex-1 flex-col items-center gap-0.5 pt-2 pb-1.5 text-[11px] font-bold ${
                active ? 'text-primary' : 'text-ink-faint'
              }`}
            >
              {item.icon('h-6 w-6')}
              {item.label}
            </button>
          );
        })}
      </div>
    </nav>
  );
}
